import React from 'react';
import { Field as FinalField } from 'react-final-form';
import type { FieldState } from 'final-form';
import { omit } from 'lodash';
import getColour from '@lendi-ui/commons/colours';
import Field from '@lendi-ui/field';
import { InputProp } from '@lendi-ui/text-input';

import { UseNumeralOptions, useUpdatedRef } from '@funnels/hooks';
import { Body } from '@funnels/ui-components';
import {
  getAriaLabelById,
  validateNumber,
  validatePositiveNonZeroNumber,
  validatePositiveNumber,
} from '@funnels/utils';

import { FieldLabel } from '../_common/FieldLabel';
import NumericalInput from '../_common/NumericalInput';
import { FieldWrapper } from '../_common/style';
import { CommonFieldProps } from '../_common/types';

export interface NumericalInputFieldProps extends CommonFieldProps, Omit<InputProp, 'name' | 'onChange'> {
  className?: string;
  inputClassName?: string;
  digitsLimit?: number;
  isDecimalValid?: boolean;
  isOptional?: boolean;
  isZeroValid?: boolean;
  isNegativeValueValid?: boolean;
  maxValue?: string;
  minValue?: string;
  helpText?: string;
  onChange?: (value?: number) => void;
  format?: (value?: number, name?: string) => number | undefined;
  parse?: (value?: number, name?: string) => number | undefined;
}

const NumericalInputField = (props: NumericalInputFieldProps) => {
  const {
    className,
    inputClassName,
    digitsLimit,
    isDecimalValid,
    isDisabled,
    isOptional,
    isZeroValid,
    isNegativeValueValid,
    label,
    labelAfter,
    maxValue,
    minValue,
    name,
    subLabel,
    helpText,
    format,
    parse,
    validate: validateFn,
    validationFailMessage,
    disableFailMessage = false,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    ...otherProps
  } = props;
  const digitsLimitProps = digitsLimit ? { maxLength: digitsLimit } : {};
  const validationRef = useUpdatedRef({ isOptional, isDisabled });
  const validate = (value: any, allValues?: Object, fieldMeta?: FieldState<any>) => {
    if (validationRef.current.isDisabled) {
      return;
    }

    if (validateFn) {
      return validateFn(value, allValues, fieldMeta);
    } else if (isNegativeValueValid) {
      return validateNumber(validationFailMessage, validationRef.current.isOptional)(value, allValues, fieldMeta);
    } else if (isZeroValid) {
      return validatePositiveNumber(validationFailMessage, validationRef.current.isOptional)(
        value,
        allValues,
        fieldMeta
      );
    } else {
      return validatePositiveNonZeroNumber(validationFailMessage, validationRef.current.isOptional)(
        value,
        allValues,
        fieldMeta
      );
    }
  };
  const fieldProps = {
    name,
    format,
    parse,
    validate,
  };
  const options: UseNumeralOptions = {
    negative: !!isNegativeValueValid,
    fixed: !!isDecimalValid,
    maxValue,
    minValue,
  };
  return (
    <FieldWrapper className={className}>
      <FieldLabel label={label} labelAfter={labelAfter} subLabel={subLabel} idPostfix={name} isOptional={isOptional} />
      <FinalField {...fieldProps}>
        {({ input, meta }) => (
          <>
            <Field {...meta} error={disableFailMessage ? undefined : meta.error}>
              <NumericalInput
                data-component={name}
                input={input}
                options={options}
                {...digitsLimitProps}
                {...omit(otherProps, 'onRemove', 'aria-labelledby', 'aria-describedby')}
                className={inputClassName}
                isDisabled={isDisabled}
                isFullWidth
                isError={!!(meta.error && meta.touched)}
                aria-labelledby={getAriaLabelById('label', label ? name : undefined, ariaLabelledby)}
                aria-describedby={getAriaLabelById('description', subLabel ? name : undefined, ariaDescribedby)}
                aria-invalid={disableFailMessage ? false : meta.error && meta.touched}
              />
            </Field>
            {helpText && !(meta.error && meta.touched === true) && (
              <Body style={{ width: '100%' }} size="xs" color={getColour('text', 'subtle')} mt="xxxs">
                {helpText}
              </Body>
            )}
          </>
        )}
      </FinalField>
    </FieldWrapper>
  );
};

export default NumericalInputField;
